<template>
  <default-card
    :header-text="`Support Tickets ${ticketCountLabel}`"
  >
    <template #card-header-actions>
      <default-button
        element="a"
        size="md"
        href="/support/create"
        :fixed-width="true"
      >
        Add Ticket
      </default-button>
    </template>

    <TabsContainer>
      <TabItem
        v-for="tab in tabs"
        :key="tab.value"
        :active="tab.value === filter"
        @click="filter = tab.value"
      >
        {{ tab.label }}
      </TabItem>
    </TabsContainer>

    <div class="search-container">
      Search Tickets

      <CustomSearch
        :default-value="searchString"
        @search="search => searchString = search"
      />
    </div>

    <support-tickets-table
      :support-tickets="supportTickets"
      :config="config"
      :filter="filter"
      :loading="isLoading"
      @order-by="setOrderBy"
    />

    <div
      v-if="!isLoading"
      class="paging-container"
    >
      <div
        v-if="hasPrevPage"
        class="paging-button prev"
        @click="prevPage()"
      >
        <i class="far fa-chevron-double-left" />
        Previous
      </div>

      <div class="paging-info">
        Page {{ page }} of {{ numberOfPages }}
      </div>

      <div
        v-if="hasNextPage"
        class="paging-button next"
        @click="nextPage()"
      >
        Next
        <i class="far fa-chevron-double-right" />
      </div>
    </div>
  </default-card>
</template>

<script>
import DefaultCard from "@/components/global/cards/DefaultCard.vue";
import SupportTicketsTable from "./table/SupportTicketsTable.vue";
import DefaultButton from "@/components/global/buttons/DefaultButton.vue";
import axios from "axios";
import TabsContainer from "@/components/tabs/TabsContainer.vue";
import TabItem from "@/components/tabs/TabItem.vue";
import CustomSearch from "../../../views/reports/components/global/forms/CustomSearch.vue";

export default {
  components: {
    CustomSearch,
    TabItem,
    TabsContainer,
    DefaultButton,
    SupportTicketsTable,
    DefaultCard,
  },
  props: {
    config: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      enableWatchers: false,
      filter: 'awaiting-review',
      supportTicketCount: 0,
      page: 1,
      numberOfPages: 1,
      supportTickets: [],
      isLoading: true,
      hasNextPage: false,
      hasPrevPage: false,
      searchString: '',
      orderBy: null,
    };
  },
  computed: {
    ticketCountLabel() {
      if(this.isLoading) {
        return '';
      }

      return `(${this.supportTicketCount})`;
    },
    tabs() {
      return Object.keys(this.config.filters).map((key) => ({
        value: key,
        label: this.config.filters[key].name
      }));
    }
  },
  watch: {
    filter: async function() {
      if (!this.enableWatchers) {
        return;
      }

      this.page = 1;
      localStorage.setItem('support-ticket-filter', this.filter);
      await this.getTickets();
    },

    searchString: async function() {
      if (!this.enableWatchers) {
        return;
      }

      this.page = 1;
      localStorage.setItem('support-ticket-search', this.searchString);
      await this.getTickets();
    },

    page: function() {
      if (!this.enableWatchers) {
        return;
      }

      localStorage.setItem('support-ticket-page', this.page);
    }
  },
  created() {
    const storedFilter = localStorage.getItem('support-ticket-filter');
    const storedSearch = localStorage.getItem('support-ticket-search');
    const storedPage = localStorage.getItem('support-ticket-page');

    if (storedFilter) {
      this.filter = storedFilter;
    }

    if (storedSearch) {
      this.searchString = storedSearch;
    }

    if (storedPage) {
      this.page = storedPage;
    }
  },
  async mounted() {
    await this.getTickets();

    this.enableWatchers = true;
  },
  methods: {
    async getTickets() {
      this.isLoading = true;

      const request = await axios.get('/ajax/support', {
        params: {
          filter: this.filter,
          search: this.searchString,
          page: this.page,
          orderBy: this.orderBy,
        },
      });

      this.supportTickets = request.data.data;
      this.supportTicketCount = request.data.meta.total;
      this.numberOfPages = request.data.meta.last_page;
      this.hasNextPage = request.data.links.next !== null;
      this.hasPrevPage = request.data.links.prev !== null;

      this.isLoading = false;
    },

    async nextPage() {
      this.page++;
      await this.getTickets();
    },

    async prevPage() {
      this.page--;
      await this.getTickets();
    },

    setOrderBy(orderBy) {
      this.orderBy = {
        column: this.config.filters[this.filter].columns[orderBy.column].column,
        direction: orderBy.direction,
      }
      this.getTickets();
    }
  }
}
</script>

<style scoped>
  .search-container {
    margin: 20px 0;
    display: flex;
    align-items: center;
  }

  .paging-container {
    margin-top: 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 30px;
    font-size: 16px;
  }

  .paging-info {
    flex: 1 1 0;
    text-align: center;
  }

  .paging-button {
    transition: all 0.3s ease;
    cursor: pointer;
  }

  .paging-button:hover {
    color: #0094FF;
  }

  .paging-button.prev i {
    margin-right: 10px;
  }

  .paging-button.next i {
    margin-left: 10px;
  }
</style>

<style>
.input_styles.search_icon {
  height: 1rem;
  margin-left: 10px;
}
</style>
